import { CheckCircle, InfoRounded } from "@mui/icons-material";
import { Alert, Grid, TextField } from "@mui/material";
import _ from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { ExternalLink } from "../../../../common/components/ExternalLink";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { EmptyInfo } from "../../../components/Empty";
import { QuestionLabel } from "../../../components/get-compliant/survey/QuestionContainer";
import { GetCompliantPageWrapper } from "../../../components/layout/GetCompliantPageWrapper";
import { PageHeader } from "../../../components/layout/header/Header";
import { SurveyGateButton } from "../../../components/organisms/survey/SurveyGateButton";
import {
  useOrganizationVendors,
  usePrimaryOrganization,
  usePrimaryOrganizationId,
} from "../../../hooks";
import {
  UpdateVendorFn,
  useUpdateVendor,
} from "../../../hooks/get-compliant/vendors/useUpdateVendor";
import { DataRecipientDocumentationCard } from "../../../components/organisms/data-recipients/DataRecipientDocumentationCard";
import { TipBox } from "../../../components/TipBox";
import { UploadVendorAgreementResponse } from "../../../../common/service/server/controller/StorageController";
import { Api } from "../../../../common/service/Api";
import { Callout, CalloutVariant } from "../../../components/Callout";
import { SafeCountriesDialog } from "../../../components/dialog/SafeCountriesDialog";
import { GDPRSccDocumentationContactLocator } from "../../../components/get-compliant/vendors/GDPRSccDocumentationContactLocator";
import validator from "validator";
import { DisableableButton } from "../../../components";
import { EmailRecipientsDialog } from "../../../components/dialog/EmailRecipientsDialog";
import { Error as RequestsError } from "../../../copy/requests";

export const VENDOR_CLASSIFICATION_SURVEY_NAME = "vendor-classification-survey";

export const GDPRInternationalDataTransfers = () => {
  // Variables
  const orgId = usePrimaryOrganizationId();
  const [org, orgRequest] = usePrimaryOrganization();
  const [safeCountriesDialogOpen, setSafeCountriesDialogOpen] = useState(false);
  const [emailRecipientsDialogOpen, setEmailRecipientsDialogOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailBody, setEmailBody] = useState(
    "Hello,\n\nYou are one of our providers who processes personal data and we are trying to determine if you have Standard Contractual Clauses (SCCs) in place that govern our transfer of personal data to you from the EEA/UK/CH, in accordance with GDPR. If you have SCCs in place, could you please direct me to the supporting documentation?",
  );

  // Requests
  const [dataRecipients, orgVendorsRequest, refresh] = useOrganizationVendors(orgId);
  const { updateVendor } = useUpdateVendor(orgId, dataRecipients);

  const relevantRecipients = useMemo(
    () => dataRecipients.filter((dr) => dr.disclosedToByEeaUkGroup && dr.gdprNeedsSccs),
    [dataRecipients],
  );

  const [recipientsWithSccs, recipientsWithoutSccs] = useMemo(
    () =>
      _.partition(
        relevantRecipients,
        (dr) => dr.gdprHasSccs || dr.vendorGdprDpfUrl || dr.vendorGdprSccUrl,
      ),
    [relevantRecipients],
  );

  const [emailContacts, setEmailContacts] = useState<
    {
      email: string;
      vendorId: string;
      recipient: OrganizationDataRecipientDto;
    }[]
  >(
    recipientsWithoutSccs
      .filter(
        (recipient) =>
          recipient.gdprSccVendorReviewStatus === "VENDOR_CONTACTED" &&
          (recipient.email || recipient.defaultEmail),
      )
      .map((recipient) => ({
        email: recipient.email || recipient.defaultEmail,
        vendorId: recipient.vendorId,
        recipient,
      })),
  );

  const showContactForm = recipientsWithoutSccs.some(
    (recipient) => recipient.gdprSccVendorReviewStatus === "VENDOR_CONTACTED",
  );

  const updateVendorDebounced = useCallback(_.debounce(updateVendor, 300, { maxWait: 1000 }), [
    updateVendor,
  ]);

  const onContactUpdate = async (recipient: OrganizationDataRecipientDto, email: string) => {
    const newContacts = [
      ...emailContacts.filter((contact) => contact.vendorId != recipient.vendorId),
    ];
    if (email || recipient.defaultEmail) {
      newContacts.push({
        email: email || recipient.defaultEmail,
        vendorId: recipient.vendorId,
        recipient,
      });
    }
    setEmailContacts(newContacts);
    await updateVendorDebounced({
      vendorId: recipient.vendorId,
      email: email || recipient.defaultEmail,
    });
  };

  const {
    answers,
    setAnswer,
    request: surveyRequest,
  } = useSurvey(orgId, VENDOR_CLASSIFICATION_SURVEY_NAME);

  const vendorsToContact = useMemo<
    {
      email: string;
      vendorId: string;
      recipient: OrganizationDataRecipientDto;
    }[]
  >(() => {
    const res = [];
    for (const ec of emailContacts) {
      const match = recipientsWithoutSccs.find((recipient) => recipient.vendorId === ec.vendorId);
      if (
        match &&
        !match.gdprSccVendorReviewContacted &&
        match.gdprSccVendorReviewStatus === "VENDOR_CONTACTED"
      ) {
        res.push({
          ...ec,
          recipient: match,
        });
      }
    }
    return res;
  }, [emailContacts, recipientsWithoutSccs]);

  const contactedVendors = useMemo<
    {
      email: string;
      vendorId: string;
      recipient: OrganizationDataRecipientDto;
    }[]
  >(
    () =>
      recipientsWithoutSccs
        .filter(
          (r) =>
            r.gdprSccVendorReviewContacted && r.gdprSccVendorReviewStatus === "VENDOR_CONTACTED",
        )
        .map((recipient) => {
          let email = recipient.email || recipient.defaultEmail;
          const contact = emailContacts.find((ec) => ec.vendorId === recipient.vendorId);
          if (contact?.email) {
            email = contact.email;
          }
          return {
            email,
            vendorId: recipient.vendorId,
            recipient,
          };
        }),
    [emailContacts, recipientsWithoutSccs],
  );

  const dispatchEmail = async () => {
    setEmailError(false);
    setEmailLoading(true);
    const vendors = vendorsToContact
      .filter(({ email }) => Boolean(email) && validator.isEmail(email))
      .map((v) => v.vendorId);
    try {
      const res = await Api.organizationVendor.requestDocumentation(org.id, vendors, emailBody);
      if (!res?.ok) {
        setEmailError(true);
      }
      await refresh();
    } catch (e) {
      setEmailError(true);
    } finally {
      setEmailLoading(false);
    }
  };

  const canEmail = useMemo<boolean>(
    () =>
      recipientsWithoutSccs.every((recipient) => {
        if (
          ["FULFILLMENT_ONLY", "SAFE_COUNTRIES_ONLY", "DOCS_PROVIDED"].includes(
            recipient?.gdprSccVendorReviewStatus,
          )
        ) {
          return true;
        }
        if (
          recipient.gdprSccVendorReviewStatus === "VENDOR_CONTACTED" &&
          (recipient.email || recipient.defaultEmail)
        ) {
          return true;
        }
        return false;
      }),
    [recipientsWithoutSccs],
  );

  return (
    <GetCompliantPageWrapper requests={[surveyRequest, orgVendorsRequest, orgRequest]}>
      <PageHeader
        titleContent="Data Transfers"
        descriptionContent={
          <>
            <p>
              The GDPR imposes special rules on the transfer of personal data outside of the
              EEA/UK/CH. The US is considered an “unsafe” location. If your business uses vendors
              who process European consumer data on US-based servers, your business is required to
              take additional compliance steps.
            </p>
            <p>
              We’ll review your vendors to determine if they process personal data in unsafe
              countries. Vendors that process data in “safe” countries are excluded from this step.
            </p>
          </>
        }
      />
      <div className="mt-xl">
        <SurveyGateButton
          survey={answers}
          slug="gdpr-international-data-transfers-get-started"
          updateResponse={setAnswer}
          label="Review My Vendors"
        >
          <div className="mb-xl">
            <div className="d-flex align-items-center mb-mdlg text-t5">
              <CheckCircle color="success" />
              <div className="ml-xs w-100 color-green-600 text-weight-bold">Safe Transfer</div>
            </div>
            <DataRecipientsSccRecommendation
              organizationId={orgId}
              recipients={recipientsWithSccs}
              updateVendor={updateVendor}
              refresh={refresh}
            />
          </div>

          <div className="mb-xl">
            <div className="d-flex align-items-center mb-mdlg text-t5">
              <InfoRounded color="error" />
              <div className="ml-xs w-100 color-red-600 text-weight-bold">Needs Review</div>
            </div>
            <DataRecipientsSccSelection
              organizationId={orgId}
              openSafeCountriesDialog={() => {
                setSafeCountriesDialogOpen(true);
              }}
              recipients={recipientsWithoutSccs}
              updateVendor={updateVendor}
              refresh={refresh}
              onContactUpdate={onContactUpdate}
            />
          </div>

          {showContactForm && (
            <>
              <div className={`mb-xl ${emailLoading ? "opacity-50" : ""}`}>
                <form>
                  <div className="d-flex align-items-center mb-mdlg text-t5">
                    <div className="ml-xs w-100 text-weight-bold">Contact Unconfirmed Vendors</div>
                  </div>
                  <Callout className="p-mdlg mb-md" variant={CalloutVariant.LightGray}>
                    <p>
                      To:{" "}
                      {vendorsToContact.filter(
                        ({ email }) => Boolean(email) && validator.isEmail(email),
                      ).length > 0 ? (
                        <span
                          className="text-weight-medium cursor-pointer text-decoration-underline color-secondary-600"
                          onClick={() => {
                            setEmailRecipientsDialogOpen(true);
                          }}
                        >
                          see recipients
                        </span>
                      ) : (
                        <span className="text-weight-medium">No valid recipients</span>
                      )}
                    </p>
                    <p className="text-weight-bold">
                      Subject: International Data Transfer Safety Clauses
                    </p>
                    <TextField
                      className="background-color-white"
                      multiline={true}
                      fullWidth={true}
                      onChange={(e) => {
                        setEmailBody(e.target.value);
                      }}
                      value={emailBody}
                    />
                    <p>Thank you,</p>
                    <p>{org.name} Privacy Team</p>
                  </Callout>
                  <div className="d-flex justify-end">
                    <DisableableButton
                      color="primary"
                      variant="contained"
                      size="medium"
                      onClick={dispatchEmail}
                      className="w-192"
                      disabled={
                        emailLoading ||
                        !canEmail ||
                        !vendorsToContact.length ||
                        !vendorsToContact.every(({ email }) => {
                          return Boolean(email) && validator.isEmail(email);
                        }) ||
                        !org.mailbox?.canSendEmail
                      }
                      disabledTooltip={
                        !org.mailbox?.canSendEmail
                          ? "You cannot send an email while your privacy email is disconnected."
                          : "You must make a selection for each vendor and fill in needed details."
                      }
                    >
                      Email Vendors
                    </DisableableButton>
                  </div>
                </form>
              </div>
              {contactedVendors.length > 0 && (
                <Alert className="mb-mdlg" color="success">
                  Emails were sent to: {contactedVendors.map((v) => v.recipient.name).join(", ")}
                </Alert>
              )}
              {emailError && (
                <Alert className="mb-mdlg" color="error">
                  {RequestsError.ContactVendorsMissingSCC}
                </Alert>
              )}
            </>
          )}
        </SurveyGateButton>
        <SafeCountriesDialog
          open={safeCountriesDialogOpen}
          onClose={() => {
            setSafeCountriesDialogOpen(false);
          }}
        />

        <EmailRecipientsDialog
          recipients={[...vendorsToContact, ...contactedVendors]}
          open={emailRecipientsDialogOpen && !safeCountriesDialogOpen}
          onClose={() => {
            setEmailRecipientsDialogOpen(false);
          }}
        />
      </div>
    </GetCompliantPageWrapper>
  );
};

type DataRecipientsSccRecommendationProps = {
  organizationId: string;
  recipients: OrganizationDataRecipientDto[];
  updateVendor: UpdateVendorFn;
  refresh: () => void;
};
const DataRecipientsSccRecommendation: React.FC<DataRecipientsSccRecommendationProps> = ({
  organizationId,
  recipients,
  updateVendor,
  refresh,
}) => {
  if (recipients.length < 1) {
    return (
      <>
        <QuestionLabel label="Data Recipients with Standard Contractual Clauses" />
        <EmptyInfo>
          Based on our research, none of your Data Recipients have incorporated SCCs into their
          documentation.
        </EmptyInfo>
      </>
    );
  }

  const updateUrl = async (r: OrganizationDataRecipientDto, url: string) => {
    let gdprSccUrl = url;
    if (url && !url.match(/^https?:\/\//)) {
      gdprSccUrl = `https://${gdprSccUrl}`;
    }
    await updateVendor({ vendorId: r.vendorId, gdprSccUrl });
    refresh();
  };

  const updateFile = async (
    r: OrganizationDataRecipientDto,
    name: string,
    file: File,
  ): Promise<{ fileKey: string }> => {
    let sccDocumentationResponse: UploadVendorAgreementResponse | null = null;
    if (file && name != r.gdprSccFileName) {
      sccDocumentationResponse = await Api.storage.postOrganizationVendorAgreement(
        organizationId,
        r.vendorId,
        name,
        file,
      );
      if (!sccDocumentationResponse?.fileKey || sccDocumentationResponse?.error) {
        throw new Error(sccDocumentationResponse?.error ?? "File upload unsuccessful");
      }
      await updateVendor({
        vendorId: r.vendorId,
        gdprSccFileName: name,
        gdprSccFileKey: sccDocumentationResponse.fileKey,
      });
      refresh();
      return {
        fileKey: sccDocumentationResponse.fileKey,
      };
    }
    return { fileKey: "" };
  };

  const clear = async (r: OrganizationDataRecipientDto) => {
    await updateVendor({
      vendorId: r.vendorId,
      gdprSccUrl: null,
      gdprSccFileName: null,
      gdprSccFileKey: null,
    });
    refresh();
  };

  return (
    <>
      <div>
        <p className="mb-lg">
          Based on our research, the vendors below have incorporated EU-US Data Privacy Framework
          Certification (DPF Certification) or Standard Contractural Clauses (SCCs) into their terms
          of service. You can review the DPF/SCC documentation we found and add your own supporting
          documents or links if you’d like.
        </p>
        <Grid container spacing={2}>
          {recipients.map((r) => {
            return (
              <DataRecipientDocumentationCard
                organizationId={organizationId}
                key={r.id}
                recipient={r}
                onUrlChange={(url) => updateUrl(r, url)}
                onFileDelete={() => clear(r)}
                onFileUpload={(fileName, file) => updateFile(r, fileName, file)}
              />
            );
          })}
        </Grid>
      </div>
    </>
  );
};

type DataRecipientsSccSelectionProps = {
  organizationId: string;
  openSafeCountriesDialog: () => void;
  recipients: OrganizationDataRecipientDto[];
  updateVendor: UpdateVendorFn;
  refresh: () => void;
  onContactUpdate: (recipient: OrganizationDataRecipientDto, email: string) => Promise<void>;
};
const DataRecipientsSccSelection: React.FC<DataRecipientsSccSelectionProps> = ({
  organizationId,
  openSafeCountriesDialog,
  recipients,
  updateVendor,
  refresh,
  onContactUpdate,
}) => {
  const updateUrl = async (r: OrganizationDataRecipientDto, url: string) => {
    let gdprSccUrl = url;
    if (url && !url.match(/^https?:\/\//)) {
      gdprSccUrl = `https://${gdprSccUrl}`;
    }
    await updateVendor({ vendorId: r.vendorId, gdprSccUrl });
    refresh();
  };

  const updateFile = async (r: OrganizationDataRecipientDto, name: string, file: File) => {
    let sccDocumentationResponse: UploadVendorAgreementResponse | null = null;
    if (file && name != r.gdprSccFileName) {
      sccDocumentationResponse = await Api.storage.postOrganizationVendorAgreement(
        organizationId,
        r.vendorId,
        name,
        file,
      );
      if (!sccDocumentationResponse?.fileKey || sccDocumentationResponse?.error) {
        throw new Error(sccDocumentationResponse?.error ?? "File upload unsuccessful");
      }
      await updateVendor({
        vendorId: r.vendorId,
        gdprSccFileName: name,
        gdprSccFileKey: sccDocumentationResponse.fileKey,
      });
      refresh();
      return {
        fileKey: sccDocumentationResponse.fileKey,
      };
    }
    return { fileKey: "" };
  };

  const clear = async (r: OrganizationDataRecipientDto) => {
    await updateVendor({
      vendorId: r.vendorId,
      gdprSccUrl: null,
      gdprSccFileName: null,
      gdprSccFileKey: null,
    });
    refresh();
  };

  return (
    <>
      <div>
        <p>
          We could not confirm DPF certifications or SCCs for the following data recipients. You can
          search the{" "}
          <ExternalLink href="https://www.dataprivacyframework.gov/s/participant-search">
            DPF participant list
          </ExternalLink>{" "}
          for your vendor and add the URL or upload your own documentation (if available) below.
          Otherwise, or select “contact the vendor” to ask about data transfer safeguards.
        </p>

        <TipBox
          title="Vendors marked with this icon have been flagged by our team as not requiring documentation."
          className="mb-lg"
        >
          When necessary to perform a contract with the consumer, such as fulfilling an order, the
          data transfer rules do not apply to the vendor receiving the data in a foreign country.
        </TipBox>

        <GDPRSccDocumentationContactLocator
          organizationId={organizationId}
          openSafeCountriesDialog={openSafeCountriesDialog}
          recipients={recipients}
          updateVendor={updateVendor}
          refresh={refresh}
          onContactUpdate={onContactUpdate}
          onUrlChange={updateUrl}
          onFileDelete={clear}
          onFileUpload={updateFile}
        />
      </div>
    </>
  );
};
